import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { updateMessages, toggleModal } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { setExtra, addExtras } from 'apps/auth/actions/extraAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { PanelWithCheck } from 'apps/auth/components/common/components/PanelWithCheck/PanelWithCheck';
import BaseLayout from 'apps/auth/components/common/Layout/BaseLayout';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import Form from 'apps/auth/components/form-multi/Form';
import { getField } from 'apps/auth/components/form-multi/utils';
import { getHelpMobileIdParams } from 'apps/auth/components/utils/AmplitudeUtils';
import BasePage from 'apps/auth/components/utils/BasePage';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import Spinner from 'apps/auth/components/utils/Spinner/Spinner';
import {
    ACCOUNT_PURPOSE_CONTINUE_CLICKED,
    ACCOUNT_PURPOSE_SCREEN_SHOWN,
    GET_HELP_CLICKED,
    INCREASE_LIMITS_SHOWN,
    NOT_NOW_CLICKED,
    VERIFICATION_FAILED_SCREEN_SHOW,
} from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { KYC_PROVIDER } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { SUMSUB } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { PURPOSE_ACCOUNT_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_PURPOSE_OF_ACCOUNT_UPDATE_ACTION, AUTH_SEND_MAIN_LINK, HELP_MOBILE } from 'apps/auth/constants/UrlConstants';
import { getLangCountryPrefix } from 'apps/auth/helpers/utils';
import { useApp, useExtra, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';

import CommonIntro from './CommonIntro';
import RetryConfirmIntro from './RetryConfirmationIntro';

const KYCLimit = (props) => {
    const PRELOADER = 'PRELOADER';
    const PURPOSE_OF_ACCOUNT = 'PURPOSE_OF_ACCOUNT';
    const COMMON_INTRO = 'COMMON_INTRO';
    const RETRY_CONFIRMATION = 'RETRY_CONFIRMATION';

    const {
        continueButtonLink,
        purposeOfAccount,
        notNowLink,
        retryConfirmation,
        onfidoCheckError,
        onfidoTooManyAttempts,
        hasSumsub,
        sumsubInfo,
        helpCenterLink,
        showPurposeOfAccount,
    } = useExtra();

    const { country } = useApp();
    const { lang } = useLanguage();

    const { notNowButton, screenTitle, screenText, agreeButton, needHelpWithDocument, continueButton, goBack } = usePageMessages();
    const [cancellationReason, setCancellationReason] = useState([]);
    const [disabled, setDisabled] = useState(true);
    const submitRef = useRef(null);
    const popupRef = useRef(null);

    const getFirstScreen = () => {
        if (retryConfirmation || onfidoTooManyAttempts) {
            return RETRY_CONFIRMATION;
        }
        return COMMON_INTRO;
    };

    const openHelpCenter = (e) => {
        e.preventDefault();
        const link = getLangCountryPrefix(lang, country) + HELP_MOBILE + helpCenterLink || '';
        amplitude.getInstance().logEvent(GET_HELP_CLICKED, getHelpMobileIdParams(link));
        props.toggleSpinner(true);
        window.location.href = link;
    };

    const [screen, setScreen] = useState(getFirstScreen());

    useEffect(() => {
        const param = hasSumsub ? { [KYC_PROVIDER]: SUMSUB } : null;
        if (onfidoCheckError || retryConfirmation) {
            amplitude.getInstance().logEvent(VERIFICATION_FAILED_SCREEN_SHOW, param);
        } else {
            amplitude.getInstance().logEvent(INCREASE_LIMITS_SHOWN, param);
        }
    }, []);

    const getRetryText = () => {
        if (hasSumsub && sumsubInfo?.comment) {
            return sumsubInfo?.comment;
        } else {
            return screenText;
        }
    };

    const onClickNotNow = () => {
        props.toggleModal(false);
    };

    const submitHandler = (fields, json) => {
        if (json && json.messageBodyMessages) {
            props.updateMessages(json.messageBodyMessages);
        }
        if (json && json.extra && json.extra.errorCode) {
            props.toggleModal(
                true,
                <ModalComponent canCloseModal={true} isVerticalCentered>
                    <InfoPage
                        insideModalComponent={true}
                        animatedIconFail
                        headerText={json.messages?.error_title}
                        mainText={json.messages?.errorText}
                        primaryButtonCaption={goBack}
                        primaryButtonType="button"
                        primaryButtonOnClick={onClickNotNow}
                    />
                </ModalComponent>,
            );
        } else if (json && json.extra) {
            setScreen(PRELOADER);
            props.addExtras(json.extra).then(() => {
                window.location.href = continueButtonLink;
            });
        }
    };

    useEffect(() => {
        if (screen === PURPOSE_OF_ACCOUNT) {
            amplitude.getInstance().logEvent(ACCOUNT_PURPOSE_SCREEN_SHOWN);
        }
    }, [screen]);

    useEffect(() => {
        setDisabled(cancellationReason.length <= 0);
    }, [cancellationReason]);

    const selectItem = (value) => {
        const reasonsArr = [...cancellationReason];
        const index = reasonsArr.indexOf(value);
        if (index > -1) {
            reasonsArr.splice(index, 1);
        } else {
            reasonsArr.push(value);
        }

        setCancellationReason(reasonsArr);

        const purposes = getField('purposes', reasonsArr);

        const fields = {
            [purposes.name]: purposes,
        };
        props.setNewForm(PURPOSE_ACCOUNT_FORM_NAME, fields);
    };

    const sendPurpose = () => {
        amplitude.getInstance().logEvent(ACCOUNT_PURPOSE_CONTINUE_CLICKED, { ['account_purpose']: cancellationReason.map((code) => code.toLowerCase()) });
    };

    const notNowButtonClick = (e) => {
        e?.preventDefault();
        props.toggleSpinner(true);
        window.location.href = notNowLink;
    };

    const componentMap = {
        [PRELOADER]: <Spinner forceShow={true} />,
        [PURPOSE_OF_ACCOUNT]: (
            <>
                <CommonPage
                    rightHeaderComponent={
                        <Button
                            type="link"
                            href={AUTH_SEND_MAIN_LINK}
                            size="tight"
                            variant="tertiary"
                            caption={notNowButton}
                            title={notNowButton}
                            onClick={(e) => {
                                e.preventDefault();
                                amplitude.getInstance().logEvent(NOT_NOW_CLICKED);
                                window.history.back();
                            }}
                        />
                    }
                >
                    <HeaderPanel title={purposeOfAccount?.title} subtitle={purposeOfAccount?.subTitle} />
                    <Form
                        formName={PURPOSE_ACCOUNT_FORM_NAME}
                        onSubmitHandler={submitHandler}
                        action={AUTH_PURPOSE_OF_ACCOUNT_UPDATE_ACTION}
                        checkNotVisibleField={false}
                    >
                        {purposeOfAccount?.reasons.map((reason, i) => {
                            return (
                                <PanelWithCheck
                                    key={`reason_${i}`}
                                    mainText={reason.description}
                                    onClick={() => selectItem(reason.code)}
                                    isSelected={cancellationReason.includes(reason.code)}
                                />
                            );
                        })}
                        <FixedBottomPanel isRightAligned>
                            <Button type="submit" raised={true} caption={continueButton} ref={submitRef} onClick={sendPurpose} disabled={disabled} />
                        </FixedBottomPanel>
                    </Form>
                </CommonPage>
                <PopupNew ref={popupRef} getPopupComponent={(component) => component} />
            </>
        ),
        [COMMON_INTRO]: (
            <CommonIntro
                icon={onfidoCheckError ? 'id-card-cross-80' : 'id-card-80'}
                title={screenTitle}
                text={screenText}
                firstButton={agreeButton}
                secondButton={notNowButton}
                showPopup={true}
                topButtonOnClick={notNowButtonClick}
                firstButtonLink={showPurposeOfAccount ? null : continueButtonLink}
                secondButtonLink={notNowLink}
                primaryButtonOnClick={() => {
                    if (showPurposeOfAccount) {
                        setScreen(PURPOSE_OF_ACCOUNT);
                    }
                }}
            />
        ),
        [RETRY_CONFIRMATION]: (
            <RetryConfirmIntro
                icon={onfidoTooManyAttempts ? 'id-card-search-80' : 'id-card-cross-80'}
                title={screenTitle}
                text={getRetryText()}
                firstButton={agreeButton}
                secondButton={notNowButton}
                topButtonOnClick={notNowButtonClick}
                link={needHelpWithDocument}
                linkClick={openHelpCenter}
                showPopup={true}
                firstButtonLink={continueButtonLink}
                secondButtonLink={notNowLink}
            />
        ),
    };

    return (
        <BaseLayout>
            <>{screen ? componentMap[screen] : null}</>
        </BaseLayout>
    );
};

export default connect(null, { toggleModal, addExtras, toggleChat, setExtra, updateMessages })(BasePage(KYCLimit));
